/**
 * epub.js — translate an EPUB without rebuilding the book.
 *
 * An EPUB is a zip of XHTML, and the same reasoning as docx.js applies: swap
 * the text nodes and every stylesheet, image, font, table of contents, page
 * break and internal link survives because none of them is ever read. The
 * alternatives all lose the book — "convert to text and back" throws away the
 * structure, and rendering to PDF throws away reflow, which is the entire point
 * of the format.
 *
 * WHICH FILES CARRY WORDS is asked of the package document rather than guessed
 * from extensions: the manifest lists every content document with its media
 * type, and a book can name them anything. Guessing `*.xhtml` misses `.html`,
 * `.htm`, and the numbered files some converters emit.
 *
 * BLOCKS, NOT NODES. `<p>Press <em>Save</em> to continue.</p>` is three text
 * nodes and one sentence. Sending the nodes separately is how a translated book
 * ends up as word salad around its own italics. The block is translated whole
 * and placed in its longest text node; the others are emptied and COUNTED, the
 * same visible compromise docx.js makes, because word order moves under
 * translation and inline emphasis cannot follow its word without aligning two
 * languages.
 *
 * Exposed as window.DTEpub.
 */
(function (root) {
    'use strict';
    if (root.DTEpub) return;

    var HAS_LETTER = /\p{L}/u;

    /* Elements whose content is a unit of prose. Anything else is inline and
       stays inside its block. */
    var BLOCK = /^(p|h[1-6]|li|dt|dd|td|th|blockquote|figcaption|caption|div|section|article|aside|header|footer|nav|title)$/i;

    /* Elements whose text is code or markup, never language. */
    var OPAQUE = /^(script|style|svg|math|code|pre|kbd|samp)$/i;

    function unescapeXml(s) {
        return String(s)
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
            .replace(/&nbsp;/g, ' ')
            .replace(/&#(\d+);/g, function (_, d) { return String.fromCodePoint(+d); })
            .replace(/&#x([0-9a-f]+);/gi, function (_, h) { return String.fromCodePoint(parseInt(h, 16)); })
            .replace(/&amp;/g, '&');
    }

    function escapeXml(s) {
        return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * Split an XHTML document into an ordered list of pieces: raw markup and
     * text runs. Rebuilding is `pieces.map(...).join('')`, so anything not a
     * text run comes back byte-for-byte.
     */
    function splitText(xml) {
        var pieces = [];
        var re = /<[^>]*>/g;
        var last = 0, m;
        var opaqueDepth = 0;
        var stack = [];

        while ((m = re.exec(xml)) !== null) {
            if (m.index > last) {
                pieces.push({
                    text: unescapeXml(xml.slice(last, m.index)),
                    raw: null,
                    opaque: opaqueDepth > 0,
                    block: stack.length ? stack[stack.length - 1] : 0,
                });
            }
            var tag = m[0];
            pieces.push({ raw: tag });

            var name = (tag.match(/^<\/?\s*([a-z0-9:]+)/i) || [, ''])[1];
            var closing = /^<\//.test(tag);
            var selfClosing = /\/>$/.test(tag) || /^<[!?]/.test(tag);

            if (name && !selfClosing) {
                if (closing) {
                    if (OPAQUE.test(name) && opaqueDepth > 0) opaqueDepth--;
                    if (BLOCK.test(name) && stack.length) stack.pop();
                } else {
                    if (OPAQUE.test(name)) opaqueDepth++;
                    // Every block gets its own id so nested blocks (a <div>
                    // around <p>s) do not merge into one giant unit.
                    if (BLOCK.test(name)) stack.push(pieces.length);
                }
            }
            last = m.index + m[0].length;
        }
        if (last < xml.length) {
            pieces.push({
                text: unescapeXml(xml.slice(last)), raw: null,
                opaque: opaqueDepth > 0, block: stack.length ? stack[stack.length - 1] : 0,
            });
        }
        return pieces;
    }

    /** Text pieces grouped by their innermost block. */
    function unitsForDoc(xml, partName) {
        var pieces = splitText(xml);
        var byBlock = {};
        pieces.forEach(function (p, i) {
            if (p.raw !== null && p.raw !== undefined) return;
            if (p.opaque) return;
            if (!HAS_LETTER.test(p.text)) return;
            (byBlock[p.block] = byBlock[p.block] || []).push(i);
        });

        var units = [];
        Object.keys(byBlock).forEach(function (key) {
            var idxs = byBlock[key];
            var whole = idxs.map(function (i) { return pieces[i].text; }).join('');
            if (!HAS_LETTER.test(whole)) return;
            var longest = idxs.reduce(function (a, b) {
                return pieces[b].text.length > pieces[a].text.length ? b : a;
            });
            units.push({
                part: partName,
                idxs: [longest],
                blank: idxs.filter(function (i) { return i !== longest; }),
                text: whole,
                flattened: idxs.length > 1,
                order: longest,
            });
        });
        units.sort(function (a, b) { return a.order - b.order; });
        return { pieces: pieces, units: units };
    }

    /** Content documents, from the manifest — never from the file extension. */
    async function contentDocs(zip) {
        var container = zip.file('META-INF/container.xml');
        var opfPath = null;
        if (container) {
            var cx = await container.async('string');
            opfPath = (cx.match(/full-path\s*=\s*["']([^"']+)["']/i) || [, null])[1];
        }
        if (!opfPath) {
            var guess = Object.keys(zip.files).filter(function (n) { return /\.opf$/i.test(n); });
            opfPath = guess[0] || null;
        }
        if (!opfPath) return null;

        var opf = zip.file(opfPath);
        if (!opf) return null;
        var xml = await opf.async('string');
        var base = opfPath.indexOf('/') >= 0 ? opfPath.slice(0, opfPath.lastIndexOf('/') + 1) : '';

        var items = {};
        var re = /<item\b[^>]*>/gi, m;
        while ((m = re.exec(xml)) !== null) {
            var tag = m[0];
            var id = (tag.match(/\bid\s*=\s*["']([^"']+)["']/i) || [, null])[1];
            var href = (tag.match(/\bhref\s*=\s*["']([^"']+)["']/i) || [, null])[1];
            var type = (tag.match(/media-type\s*=\s*["']([^"']+)["']/i) || [, ''])[1];
            if (!id || !href) continue;
            if (!/xhtml\+xml|text\/html/i.test(type)) continue;
            items[id] = base + decodeURIComponent(href.replace(/[#?].*$/, ''));
        }

        // Reading order comes from the spine; anything the spine omits is
        // appended so no page is silently left untranslated.
        var order = [], seen = {};
        var sre = /<itemref\b[^>]*idref\s*=\s*["']([^"']+)["'][^>]*>/gi, sm;
        while ((sm = sre.exec(xml)) !== null) {
            var p = items[sm[1]];
            if (p && !seen[p] && zip.file(p)) { seen[p] = 1; order.push(p); }
        }
        Object.keys(items).forEach(function (id) {
            var pth = items[id];
            if (!seen[pth] && zip.file(pth)) { seen[pth] = 1; order.push(pth); }
        });
        return { opfPath: opfPath, docs: order, opfXml: xml };
    }

    /**
     * open(file, JSZip) → { kind, units, meta, build(translated, opts) }
     */
    async function open(file, JSZip) {
        var zip = await JSZip.loadAsync(file);
        var found = await contentDocs(zip);
        if (!found || !found.docs.length) throw new Error('not_an_epub');

        var parts = {}, units = [];
        for (var i = 0; i < found.docs.length; i++) {
            var name = found.docs[i];
            var xml = await zip.file(name).async('string');
            var r = unitsForDoc(xml, name);
            parts[name] = r.pieces;
            r.units.forEach(function (u) { u.id = units.length; units.push(u); });
        }

        return {
            kind: 'epub',
            units: units.map(function (u) { return { id: u.id, text: u.text }; }),
            meta: {
                parts: found.docs,
                paragraphs: units.length,
                flattened: units.filter(function (u) { return u.flattened; }).length,
                chars: units.reduce(function (n, u) { return n + u.text.length; }, 0),
            },

            async build(translated, opts) {
                opts = opts || {};
                units.forEach(function (u) {
                    var text = translated[u.id];
                    if (text == null || text === '') return;
                    var pieces = parts[u.part];
                    pieces[u.idxs[0]] = Object.assign({}, pieces[u.idxs[0]], { text: String(text) });
                    u.blank.forEach(function (i) {
                        pieces[i] = Object.assign({}, pieces[i], { text: '' });
                    });
                });

                found.docs.forEach(function (name) {
                    var out = parts[name].map(function (p) {
                        return (p.raw !== null && p.raw !== undefined) ? p.raw : escapeXml(p.text);
                    }).join('');
                    // A reader picks text direction and hyphenation from the
                    // document's own language. Leaving `ru` on a book that is
                    // now German is how a translated EPUB hyphenates wrongly on
                    // every page — and it costs one attribute.
                    // Rewrite the WHOLE <html> tag in one pass. Stripping the old
                    // attributes with a global regex and then prepending new ones
                    // leaves `<html xml:lang="de" lang="de" … lang="ru">` — a
                    // duplicate attribute, which is a fatal error to an XML parser
                    // and therefore a book that will not open. Every assertion
                    // still passed; only reading the output showed it.
                    if (opts.lang) {
                        out = out.replace(/<html\b[^>]*>/i, function (tag) {
                            var self = /\/>$/.test(tag);
                            var attrs = tag.slice(5, tag.length - (self ? 2 : 1))
                                .replace(/\s(?:xml:lang|lang)\s*=\s*["'][^"']*["']/gi, '');
                            return '<html xml:lang="' + opts.lang + '" lang="' + opts.lang + '"'
                                + attrs + (self ? '/>' : '>');
                        });
                    }
                    zip.file(name, out);
                });

                if (opts.lang) {
                    var opf = found.opfXml.replace(
                        /<dc:language[^>]*>[\s\S]*?<\/dc:language>/i,
                        '<dc:language>' + escapeXml(opts.lang) + '</dc:language>'
                    );
                    zip.file(found.opfPath, opf);
                }

                return zip.generateAsync({
                    type: 'blob',
                    mimeType: 'application/epub+zip',
                    compression: 'DEFLATE',
                });
            },
        };
    }

    root.DTEpub = {
        open: open,
        _splitText: splitText,
        _unitsForDoc: unitsForDoc,
        _contentDocs: contentDocs,
    };
})(typeof self !== 'undefined' ? self : this);
